import { useEffect, useState } from 'react';
import { ArrowRight, Eye, EyeOff, KeyRound, LoaderCircle, LockKeyhole, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@details-ai/contracts';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';

export function AdminLoginPage() {
  const { user, challenge, login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otp, setOtp] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  useEffect(() => {
    if (user) navigate('/app', { replace: true });
  }, [user, navigate]);

  async function submit(values: LoginInput) {
    setSubmitting(true);
    try {
      const result = await login(values.username, values.password);
      if (!result.requiresOtp) {
        toast.success('مرحبًا بك في لوحة الإدارة');
        navigate('/app/admin', { replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر تسجيل الدخول');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitOtp(event: React.FormEvent) {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      toast.error('أدخل رمز التحقق المكوّن من 6 أرقام');
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtp(otp);
      toast.success('تم التحقق وتسجيل الدخول');
      navigate('/app/admin', { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'رمز التحقق غير صالح');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="pattern relative grid min-h-screen place-items-center overflow-hidden px-4 py-10">
      <div className="absolute -right-48 -top-48 h-[32rem] w-[32rem] rounded-full bg-teal/10 blur-3xl" />
      <div className="absolute -bottom-56 -left-56 h-[34rem] w-[34rem] rounded-full bg-gold/10 blur-3xl" />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="mb-5 inline-flex items-center gap-2 text-xs font-bold text-slate-500 transition hover:text-ink"
        >
          <ArrowRight className="h-4 w-4" />
          العودة إلى الصفحة الرئيسية
        </Link>
        <section className="glass rounded-[2rem] p-6 shadow-panel sm:p-8">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-ink text-white shadow-lg shadow-ink/20">
              {challenge ? <KeyRound className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
            </div>
            <h1 className="text-2xl font-black text-ink">
              {challenge ? 'التحقق الإضافي' : 'دخول المشرف'}
            </h1>
            <p className="mt-2 text-xs font-semibold leading-6 text-slate-500">
              {challenge
                ? `أرسلنا رمزًا من 6 أرقام إلى ${challenge.destination}`
                : 'أدخل اسم المستخدم وكلمة المرور للوصول إلى لوحة الإدارة.'}
            </p>
          </div>

          {!challenge ? (
            <form className="space-y-5" onSubmit={handleSubmit(submit)}>
              <label className="block" htmlFor="admin-username">
                <span className="mb-2 block text-xs font-black text-ink">اسم المستخدم</span>
                <div className="relative">
                  <input
                    id="admin-username"
                    {...register('username')}
                    autoComplete="username"
                    className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 pe-11 text-sm font-semibold outline-none transition focus:border-teal focus:ring-4 focus:ring-teal/10"
                    placeholder="اكتب اسم المستخدم"
                  />
                  <ShieldCheck className="absolute end-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
                {errors.username && (
                  <span className="mt-1 block text-[11px] font-bold text-red-600">
                    {errors.username.message}
                  </span>
                )}
              </label>

              <label className="block" htmlFor="admin-password">
                <span className="mb-2 block text-xs font-black text-ink">كلمة المرور</span>
                <div className="relative">
                  <input
                    id="admin-password"
                    {...register('password')}
                    type={visible ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 pe-11 text-sm font-semibold outline-none transition focus:border-teal focus:ring-4 focus:ring-teal/10"
                    placeholder="اكتب كلمة المرور"
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((value) => !value)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-ink"
                    aria-label={visible ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                  >
                    {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <span className="mt-1 block text-[11px] font-bold text-red-600">
                    {errors.password.message}
                  </span>
                )}
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-5 py-3 text-sm font-black text-white shadow-lg shadow-ink/15 transition hover:bg-teal disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <LockKeyhole className="h-4 w-4" />}
                {submitting ? 'جارٍ التحقق…' : 'دخول المشرف'}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={submitOtp}>
              <label className="block">
                <span className="mb-2 block text-xs font-black text-ink">رمز التحقق</span>
                <input
                  id="admin-otp"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  dir="ltr"
                  className="w-full rounded-xl border border-ink/15 bg-white px-4 py-4 text-center text-2xl font-black tracking-[0.45em] outline-none transition focus:border-teal focus:ring-4 focus:ring-teal/10"
                  placeholder="000000"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-5 py-3 text-sm font-black text-white transition hover:bg-teal disabled:opacity-60"
              >
                {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                تحقق وادخل
              </button>
              <button
                type="button"
                onClick={() => void resendOtp().then(() => toast.success('أُعيد إرسال الرمز')).catch((error) => toast.error(error.message))}
                className="w-full text-center text-xs font-bold text-teal hover:underline"
              >
                إعادة إرسال الرمز
              </button>
            </form>
          )}
        </section>
        <p className="mt-5 text-center text-[11px] font-bold text-slate-400">
          الاتصال مشفر · الوصول مخصص للمشرفين المصرح لهم
        </p>
      </div>
    </main>
  );
}
